import { Icon } from '@chakra-ui/icons'
import {
  Flex,
  Button,
  ButtonGroup,
  Checkbox,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react'
import moment from 'moment'
import type { ReactElement } from 'react'
import { useCallback, useEffect } from 'react'
import { Fragment, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiChevronDown } from 'react-icons/fi'
import { MdOutlineCollections } from 'react-icons/md'
import { useHistory } from 'react-router'
import type { CellProps, Column } from 'react-table'
import { useRowSelect, useRowState } from 'react-table'
import { useTable } from 'react-table'
import { mediaCollectionsPerPage } from '../../../../../utils/mediaLibrary'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import type { MediaLibrarySearchCollectionResponse } from '../../../../interfaces'
import { MLContext, UserContext } from '../../../../providers'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import { ContextMenuTrigger } from '../../ContextMenu'
import useToast from '../../../../hooks/useToast'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import { FeaturesEnum } from '../../../../../utils/features'

const CollectionList = (): ReactElement | null => {
  const { urlPrefix } = useContext(PublicLinksInfoContext)
  const toast = useToast()
  const [
    {
      mediaCollectionList,
      loadingMediaCollections,
      selectedMediaCollections,
      mediaCollectionPage,
      abortMediaCollectionsRequest,
      urlState,
      folderPath,
      searchQuery,
    },
    dispatch,
  ] = useContext(MLContext)

  const { user } = useContext(UserContext)

  const { t } = useTranslation()

  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  interface CreatedBy {
    userId: string
    name: string
    email: string
  }

  const [gridData, setGridData] = useState({
    mediaCollectionPage,
    loadingMediaCollections: false,
    mediaCollectionList,
  })

  const columns = useMemo<Array<Column<MediaLibrarySearchCollectionResponse>>>(
    () => [
      {
        id: 'selected',
        width: '4.1em',
        Cell: ({ row }: CellProps<MediaLibrarySearchCollectionResponse>) => (
          <Checkbox isChecked={row.isSelected} pointerEvents="none" />
        ),
      },
      {
        Header: t('name', 'Name').toString(),
        accessor: values => values.name,
        Cell: ({ value }: { value: string }) => (
          <Flex align="center">
            <Icon
              as={MdOutlineCollections}
              color="appBlueyGrey"
              boxSize="1.25rem"
              marginRight="0.5rem"
            />
            <Tooltip label={value}>
              <Text className="asset-name" isTruncated>{value}</Text>
            </Tooltip>
          </Flex>
        ),
        width: '25em',
      },
      {
        Header: t('items', 'Items').toString(),
        accessor: values => values.size,
        width: '7em',
      },
      {
        Header: t('last_modified', 'Last Modified').toString(),
        accessor: values => values.updatedAt,
        Cell: ({ value }: { value: string }) => (
          <Tooltip label={moment(value).format('ddd, MMMM Do YYYY, h:mm:ss A')}>
            <Text>{moment(value).fromNow()}</Text>
          </Tooltip>
        ),
        width: '7.85em',
      },
      {
        Header: t('owner', 'Owner').toString(),
        accessor: values => values.createdBy,
        Cell: ({ value }: { value: CreatedBy | undefined }) => (
          <Tooltip label={value?.email}>
            <Text isTruncated>{value?.name ? value.name : 'NA'}</Text>
          </Tooltip>
        ),
        width: '13.85em',
      },
    ],
    [],
  )

  const data = useMemo(
    () => gridData?.mediaCollectionList,
    [gridData?.mediaCollectionList],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleRowSelected,
    dispatch: tableDispatch,
    toggleAllRowsSelected,
  } = useTable<MediaLibrarySearchCollectionResponse>(
    {
      columns,
      data,
      getRowId: row => row.mediaCollectionId,
      initialState: {
        selectedRowIds: selectedMediaCollections.reduce(
          (acc, x) => ({ ...acc, [x.mediaCollectionId]: true }),
          {},
        ),
      },
      stateReducer: (newState, action) => {
        if (action.type === 'ikSelectCollection') {
          if (!action.value) {
            dispatch({
              type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS,
              payload: selectedMediaCollections.filter(
                x => x.mediaCollectionId !== action.id,
              ),
            })
          } else {
            const newCollection = mediaCollectionList.find(
              x => x.mediaCollectionId === action.id,
            )
            if (newCollection) {
              dispatch({
                type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS,
                payload: [...selectedMediaCollections, newCollection],
              })
            }
          }
        }

        return newState
      },
    },
    useRowSelect,
    useRowState,
  )

  useEffect(() => {
    toggleAllRowsSelected(false)
    selectedMediaCollections.map(
      x => void toggleRowSelected(x.mediaCollectionId, true),
    )
  }, [selectedMediaCollections])

  // to save collection data comming from MLProvider in gridData.mediaCollectionList
  useEffect(() => {
    setGridData(prev => ({ ...prev, mediaCollectionList, mediaCollectionPage }))
  }, [mediaCollectionPage, loadingMediaCollections, mediaCollectionList])

  const generateUrl = useCallback((): string => {
    const params: Record<string, string> = {
      skip: (gridData.mediaCollectionPage * mediaCollectionsPerPage).toString(),
      limit: mediaCollectionsPerPage.toString(),
      sort: ['DESC_CREATED', 'ASC_CREATED', 'DESC_NAME', 'ASC_NAME'].includes(
        urlState.sort,
      )
        ? urlState.sort
        : 'DESC_CREATED',
      searchQuery: searchQuery,
    }
    params.path = ''

    const searchParams = new URLSearchParams(params)

    return `?${searchParams.toString()}`
  }, [
    gridData.mediaCollectionPage,
    mediaCollectionsPerPage,
    urlState,
    folderPath,
    searchQuery,
  ])

  const loadData = useCallback(() => {
    if (
      !user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      return null
    }
    fetch(`${urlPrefix}api/v2/media-collections${generateUrl()}`)
      .then(response => response.json())
      .then(data => {
        if (!Array.isArray(data)) {
          throw data
        }
        setGridData(prev => ({
          ...prev,
          loadingMediaCollections: false,
          mediaCollectionList: [...prev.mediaCollectionList, ...data],
        }))
        dispatch([{
          type: MediaLibraryActions.SET_MEDIA_COLLECTION_LIST,
          payload: [...gridData?.mediaCollectionList, ...data],
        }, {
          type: MediaLibraryActions.LOAD_MORE_MEDIA_COLLECTIONS
        }])
      })
      .catch(error => {
        toast({
          description: 'Something went wrong!',
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        setGridData(prev => ({
          ...prev,
          loadingMediaCollections: false,
          mediaCollectionList: [],
        }))
        dispatch({
          type: MediaLibraryActions.SET_MEDIA_COLLECTION_LIST,
          payload: [],
        })
      })
  }, [
    generateUrl,
    gridData.mediaCollectionList,
    dispatch,
    setGridData,
    urlState,
    user,
    FeaturesEnum,
  ])

  useEffect(() => {
    if (
      gridData.mediaCollectionPage &&
      gridData.mediaCollectionPage !== mediaCollectionPage
    ) {
      loadData()
    }
  }, [gridData.mediaCollectionPage])

  return (
    <Fragment>
      <Text fontWeight="semibold" color="appSlate">
        {t('media_collections', 'Media collections')}
      </Text>
      <Table {...getTableProps}>
        <Thead bgColor="appPaleGreyTwo">
          {headerGroups.map(headerGroup => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map((column, i) => {
                  const { key: keyCol, ...restColumn } = column.getHeaderProps()
                  return (
                    <Th
                      key={keyCol}
                      {...restColumn}
                      width={i === 0 ? 10 : column.width}
                    >
                      {column.render('Header')}
                    </Th>
                  )
                })}
              </Tr>
            )
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row)
            const { key, ...restRowProps } = row.getRowProps()
            return (
              <ContextMenuTrigger
                key={key}
                onTrigger={event => {
                  if (
                    !selectedMediaCollections
                      .map(x => x.mediaCollectionId)
                      .includes(row.original.mediaCollectionId)
                  ) {
                    dispatch([
                      {
                        type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
                        payload: {
                          mediaCollection: row.original,
                          event,
                        },
                      },
                      {
                        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                      },
                    ])
                  }
                }}
                dispatch={dispatch}
              >
                {contextMenuProps => (
                  <Tr
                    {...restRowProps}
                    data-id={`mediaCollection-${row.original.mediaCollectionId}`}
                    className="mouse-select__selectable"
                  >
                    {row.cells.map(cell => {
                      const { key: keyRow, ...restCellProps } =
                        cell.getCellProps()
                      return (
                        <Td
                          key={keyRow}
                          {...restCellProps}
                          width={cell.column.width}
                          maxW="32em"
                          cursor="pointer"
                          onClick={() => {
                            toggleRowSelected(
                              row.original.mediaCollectionId,
                              !row.isSelected,
                            )
                            tableDispatch({
                              type: 'ikSelectCollection',
                              id: row.original.mediaCollectionId,
                              value: !row.isSelected,
                            })
                          }}
                          onDoubleClick={() => {
                            dispatch([
                              {
                                type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                              },
                              {
                                type: MediaLibraryActions.RESET_MEDIA_COLLECTION_SEARCH,
                              },
                            ])
                            abortMediaCollectionsRequest()
                            history.push(
                              `/media-collections/${row.original.mediaCollectionId}/${queryFromUrl}`,
                            )
                          }}
                          {...contextMenuProps}
                        >
                          <Text isTruncated>{cell.render('Cell')}</Text>
                        </Td>
                      )
                    })}
                  </Tr>
                )}
              </ContextMenuTrigger>
            )
          })}
        </Tbody>
      </Table>
      {(gridData.mediaCollectionList.length % mediaCollectionsPerPage === 0 &&
        gridData.mediaCollectionList.length / mediaCollectionsPerPage ===
          gridData.mediaCollectionPage + 1) ||
      gridData.loadingMediaCollections ? (
        <Button
          as="a"
          variant="link"
          cursor="pointer"
          color="appClearBlue"
          onClick={() => {
            setGridData(prev => ({
              ...prev,
              mediaCollectionPage: prev.mediaCollectionPage + 1,
              loadingMediaCollections: true,
            }))
          }}
          isLoading={gridData.loadingMediaCollections}
          loadingText={t('loading', 'Loading...')}
        >
          {t('see_more', 'See More')}
          <Icon as={FiChevronDown} />
        </Button>
      ) : null}
    </Fragment>
  )
}

export default CollectionList
